import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { fireAdsConversion } from "../lib/analytics";
import {
  ShieldCheck, Sparkles, ListChecks, Plus, ArrowRight, Coins,
  Lock, X, Loader2, Trash2,
} from "lucide-react";

const fmtMoney = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
const fmtPer = (c) => {
  const v = (c / 100).toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
  return `$${v}/each`;
};

function BuyCreditsModal({ open, onClose, packs, onBought }) {
  const [busyKey, setBusyKey] = useState(null);
  const buy = async (pack) => {
    setBusyKey(pack.key);
    try {
      const here = window.location.origin + "/validations";
      const { data } = await api.post("/validations/credits/checkout", {
        key: pack.key,
        success_url: `${here}?purchase=1&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: here,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusyKey(null);
    }
  };
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="buy-credits-modal"
    >
      <div
        className="bg-white rounded-3xl max-w-2xl w-full p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#0F172A] tracking-tight">Buy validation credits</h2>
            <p className="text-sm text-[#475569] mt-1">
              One-time purchase. Credits never expire. Used to verify deliverability of any email address.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#475569] hover:bg-slate-100 p-1.5 rounded-lg"
            data-testid="buy-credits-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {packs.map((p) => (
            <button
              key={p.key}
              onClick={() => buy(p)}
              disabled={busyKey !== null}
              data-testid={`buy-pack-${p.key}`}
              className="text-left border-2 border-[#E2E8F0] hover:border-[#F26953] rounded-2xl p-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <div className="flex items-baseline justify-between">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{p.label}</div>
                {busyKey === p.key && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#F26953]" />}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-bold text-[#0F172A] mono">{p.credits.toLocaleString()}</span>
                <span className="text-sm text-[#475569]">validations</span>
              </div>
              <div className="mt-2 flex items-baseline justify-between">
                <span className="text-xl font-bold text-[#F26953]">{fmtMoney(p.price_cents)}</span>
                <span className="text-xs text-slate-400">{fmtPer(p.per_validation_cents)}</span>
              </div>
            </button>
          ))}
        </div>
        <p className="mt-5 text-[11px] text-slate-400">
          Powered by Mailgun. Secure checkout via Stripe. Credits added to your account immediately after payment.
        </p>
      </div>
    </div>
  );
}

function CreateListModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [rawEmails, setRawEmails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    // Extract emails from any text format (CSV, newline, comma, space, etc.)
    const emails = (rawEmails || "")
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    try {
      const { data } = await api.post("/validations/lists", {
        name: name.trim(),
        emails,
      });
      toast.success(`Created list "${data.name}" with ${data.member_count} addresses`);
      onCreated(data);
      setName(""); setRawEmails("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="create-list-modal"
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl max-w-2xl w-full p-7 shadow-2xl"
      >
        <div className="flex items-start justify-between mb-5">
          <h2 className="text-2xl font-bold text-[#0F172A] tracking-tight">New validation list</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="create-list-close"
            className="text-[#475569] hover:bg-slate-100 p-1.5 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">List name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Newsletter migration — Sept 2026"
              data-testid="create-list-name"
              className="mt-1 w-full px-4 py-2.5 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F26953]/30 focus:border-[#F26953]"
              maxLength={80}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Paste emails (one per line, or comma-separated)
            </label>
            <textarea
              value={rawEmails}
              onChange={(e) => setRawEmails(e.target.value)}
              placeholder="alice@example.com&#10;bob@example.com&#10;charlie@example.com"
              data-testid="create-list-emails"
              rows={8}
              className="mt-1 w-full px-4 py-2.5 border border-[#E2E8F0] rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#F26953]/30 focus:border-[#F26953]"
            />
            <p className="mt-1 text-[11px] text-slate-400">
              You&apos;ll choose which addresses to validate (and pay for) after the list is created.
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 text-sm font-semibold text-[#475569] hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !name.trim()}
            data-testid="create-list-submit"
            className="px-5 py-2 text-sm font-bold text-white bg-[#F26953] hover:bg-[#dd5a47] rounded-lg disabled:opacity-50"
          >
            {submitting ? "Creating…" : "Create list"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Validations() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [packs, setPacks] = useState([]);
  const [lists, setLists] = useState(null);
  const [buying, setBuying] = useState(false);
  const [creating, setCreating] = useState(false);

  const isPro = user?.role === "admin" || (user?.plan || "").toLowerCase() === "pro";

  const refreshAll = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        api.get("/validations/credits"),
        api.get("/validations/packs"),
      ]);
      setCredits(c.data);
      setPacks(p.data.packs || []);
    } catch (e) { toast.error(formatApiError(e)); }
    if (isPro) {
      try {
        const l = await api.get("/validations/lists");
        setLists(l.data.lists || []);
      } catch (e) { toast.error(formatApiError(e)); }
    } else {
      setLists([]);
    }
  }, [isPro]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Stripe checkout return — pop a toast and refresh
  useEffect(() => {
    if (params.get("purchase") === "1") {
      toast.success("Payment received! Credits will appear in a moment.");
      // poll for up to ~10s for the webhook to land
      let tries = 0;
      const id = setInterval(async () => {
        tries += 1;
        try {
          const { data } = await api.get("/validations/credits");
          if (data.balance > (credits?.balance || 0)) {
            setCredits(data);
            clearInterval(id);
            const addedCount = data.balance - (credits?.balance || 0);
            toast.success(`+${addedCount} credits added.`);
            // Fire Google Ads + GA4 purchase event for the credit-pack purchase.
            // We look at the most recent purchase entry in history to get the
            // session_id and dollar amount so the conversion has accurate value.
            const lastPurchase = (data.history || []).find((h) => h.action === "purchase");
            if (lastPurchase) {
              fireAdsConversion({
                transaction_id: lastPurchase.session_id || `validations_${Date.now()}`,
                value: (lastPurchase.cost_cents || 0) / 100,
                item_id: `validations_${lastPurchase.count}`,
                item_name: `oly Inbox · ${lastPurchase.count.toLocaleString()} email validations`,
              });
            }
          }
        } catch (_) { /* keep polling */ }
        if (tries > 8) clearInterval(id);
      }, 1500);
      // Clear query
      params.delete("purchase");
      params.delete("session_id");
      setParams(params, { replace: true });
      return () => clearInterval(id);
    }
  }, []);

  const deleteList = async (lst) => {
    if (!window.confirm(`Delete "${lst.name}"? Members and validation history are removed.`)) return;
    try {
      await api.delete(`/validations/lists/${lst.id}`);
      setLists((prev) => prev.filter((l) => l.id !== lst.id));
      toast.success(`Deleted "${lst.name}"`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2">
              <ShieldCheck className="h-8 w-8 text-[#F26953]" />
              Email Validations
            </h1>
            <p className="mt-2 text-[#475569] max-w-2xl">
              Verify deliverability before adding addresses to your forwarding lists. Catch typos,
              disposable addresses, and dead inboxes — protect your sender reputation.
            </p>
          </div>
          <Link to="/dashboard" className="text-sm text-[#475569] hover:text-[#0F172A]">← Back to dashboard</Link>
        </div>

        {/* Credit balance card */}
        <div
          className="rounded-2xl bg-white border border-[#E2E8F0] p-6 shadow-sm"
          data-testid="credits-card"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#F26953] to-[#FE9685] flex items-center justify-center">
                <Coins className="h-7 w-7 text-white" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Credit balance</div>
                <div className="mt-0.5 flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-[#0F172A] mono" data-testid="credit-balance">
                    {credits === null ? "—" : credits.balance.toLocaleString()}
                  </span>
                  <span className="text-sm text-[#475569]">validations available</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setBuying(true)}
              data-testid="buy-credits-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-[#0F172A] hover:bg-[#1e293b] rounded-xl transition-colors"
            >
              <Plus className="h-4 w-4" />
              Buy credits
            </button>
          </div>
          {credits?.history?.length > 0 && (
            <details className="mt-5 group">
              <summary className="text-xs font-semibold text-[#475569] cursor-pointer hover:text-[#0F172A]">
                Recent activity ({credits.history.length})
              </summary>
              <div className="mt-3 space-y-1.5 max-h-48 overflow-y-auto">
                {credits.history.map((h, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className={h.action === "purchase" ? "text-emerald-600 font-semibold" : h.action === "refund" ? "text-amber-600 font-semibold" : "text-[#475569]"}>
                      {h.action === "purchase" ? "+" : h.action === "refund" ? "+" : "−"}{h.count} · {h.action}
                    </span>
                    <span className="text-slate-400">{new Date(h.ts).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Pro gate or Lists */}
        {!isPro ? (
          <div
            className="mt-8 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#1e293b] text-white p-8 text-center"
            data-testid="pro-gate"
          >
            <Lock className="h-10 w-10 mx-auto opacity-60 mb-3" />
            <h2 className="text-2xl font-bold tracking-tight">Bulk validation is a Pro feature</h2>
            <p className="mt-2 text-slate-300 max-w-md mx-auto">
              Upgrade to Pro to import lists from Mailchimp, Klaviyo or anywhere else, then verify thousands
              of addresses in one click before they bounce.
            </p>
            <Link
              to="/dashboard?upgrade=1"
              data-testid="pro-upgrade-cta"
              className="inline-flex items-center gap-2 mt-5 px-6 py-3 text-sm font-bold text-[#0F172A] bg-white hover:bg-slate-100 rounded-xl transition-colors"
            >
              <Sparkles className="h-4 w-4 text-[#F26953]" />
              Upgrade to Pro
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <section className="mt-10">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <h2 className="text-xl md:text-2xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-[#F26953]" />
                Your lists {lists?.length > 0 && <span className="text-sm font-normal text-slate-400">({lists.length})</span>}
              </h2>
              <button
                onClick={() => setCreating(true)}
                data-testid="create-list-btn"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-[#F26953] hover:bg-[#dd5a47] rounded-xl transition-colors"
              >
                <Plus className="h-4 w-4" />
                New list
              </button>
            </div>
            {lists === null ? (
              <div className="py-12 text-center text-slate-400 text-sm">Loading lists…</div>
            ) : lists.length === 0 ? (
              <div className="rounded-2xl bg-white border-2 border-dashed border-[#E2E8F0] p-12 text-center">
                <ListChecks className="h-10 w-10 text-slate-300 mx-auto" />
                <p className="mt-3 text-sm text-[#475569]">No lists yet — paste a list of emails to get started.</p>
                <button
                  onClick={() => setCreating(true)}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#F26953] hover:text-[#dd5a47]"
                >
                  <Plus className="h-4 w-4" /> Create your first list
                </button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {lists.map((lst) => {
                  const validatedPct = lst.member_count > 0
                    ? Math.round((lst.validated_count / lst.member_count) * 100)
                    : 0;
                  return (
                    <div
                      key={lst.id}
                      className="bg-white rounded-2xl border border-[#E2E8F0] p-5 hover:shadow-md hover:border-[#F26953]/40 transition-all group cursor-pointer"
                      onClick={() => navigate(`/validations/${lst.id}`)}
                      data-testid={`list-card-${lst.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-[#0F172A] truncate flex-1" title={lst.name}>{lst.name}</h3>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteList(lst); }}
                          className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`list-delete-${lst.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-3 text-sm text-[#475569] mono">
                        {lst.validated_count.toLocaleString()} / {lst.member_count.toLocaleString()} validated
                      </div>
                      <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 transition-all"
                          style={{ width: `${validatedPct}%` }}
                        />
                      </div>
                      <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                        <span>{new Date(lst.created_at).toLocaleDateString()}</span>
                        <span className="inline-flex items-center gap-1 text-[#F26953] font-semibold group-hover:translate-x-0.5 transition-transform">
                          Open <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </main>

      <BuyCreditsModal
        open={buying}
        onClose={() => setBuying(false)}
        packs={packs}
        onBought={() => { setBuying(false); refreshAll(); }}
      />
      <CreateListModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(lst) => {
          setCreating(false);
          setLists((prev) => [lst, ...(prev || [])]);
        }}
      />
    </div>
  );
}
